import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import {
  CommissionPolicy,
  CommissionPolicyUpsertRequest,
  CommissionType,
  CommissionTypeList,
  SellableFeature,
  SellableFeatureList,
  calculateCommission,
} from '@/models/marketplace.model';
import { createCommissionPolicy, updateCommissionPolicy } from '@/services/marketplace.service';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, SelectPicker, NumberInput, Message } from 'rsuite';
import { toastSuccessMessage } from '@/utils/toasterMessage';

const modelSchema = Schema.Model<CommissionPolicyUpsertRequest>({
  feature: Schema.Types.NumberType().isRequired('This field is required.'),
  type: Schema.Types.NumberType().isRequired('This field is required.'),
  value: Schema.Types.NumberType().isRequired('This field is required.').min(0, 'Value cannot be negative.'),
  minimumPrice: Schema.Types.NumberType().isRequired('This field is required.').min(0, 'Minimum price cannot be negative.'),
});

const emptyModel: CommissionPolicyUpsertRequest = {
  feature: SellableFeature.AdvanceShareTemplate,
  type: CommissionType.Fixed,
  value: undefined,
  minimumPrice: undefined,
};

interface CommissionPolicyDrawerProps extends DrawerProps {
  /** The policy being edited, or undefined to create a new one. */
  policy?: CommissionPolicy;
  onSubmitted?: () => void;
}

const CommissionPolicyDrawer = (props: CommissionPolicyDrawerProps) => {
  const { open, onClose, onSubmitted, policy, ...rest } = props;

  const [formValue, setFormValue] = useState<CommissionPolicyUpsertRequest>({ ...emptyModel });
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (open) {
      setFormValue(
        policy
          ? {
              feature: policy.feature,
              type: policy.type,
              value: policy.value,
              minimumPrice: policy.minimumPrice,
            }
          : { ...emptyModel }
      );
    }
  }, [open, policy]);

  const handleClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue({ ...emptyModel });
    setErrorModel(undefined);
    setLoading(false);
    if (onClose) {
      onClose(event);
    }
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<CommissionPolicy> = policy
        ? await updateCommissionPolicy(policy.id, formValue)
        : await createCommissionPolicy(formValue);
      if (result.success) {
        toastSuccessMessage(policy ? 'Commission policy updated' : 'Commission policy created');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prev => ({
      ...prev,
      ...value,
    }));
  };

  // A percentage commission rounds down, so a minimum price that is too low would
  // burn nothing at all. Show the operator what the current settings actually do.
  const preview = () => {
    if (formValue.type == null || formValue.value == null || formValue.minimumPrice == null) return null;
    const at = calculateCommission( formValue.minimumPrice, formValue.type, formValue.value);
    return at;
  };

  const previewResult = preview();

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>{policy ? 'Edit Commission Policy' : 'Add Commission Policy'}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form
          name="commission-policy-form"
          fluid
          ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}
          disabled={loading}
        >
          <Form.Group>
            <Form.Label>Feature</Form.Label>
            <Form.Control
              name="feature"
              accepter={SelectPicker}
              data={SellableFeatureList}
              searchable={false}
              block
              disabled={!!policy}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Form.Control name="type" accepter={SelectPicker} data={CommissionTypeList} searchable={false} block />
          </Form.Group>
          <Form.Group>
            <Form.Label>
              {formValue.type === CommissionType.Percentage ? 'Percent of each sale' : 'Points per sale'}
            </Form.Label>
            <Form.Control name="value" accepter={NumberInput} min={0} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Minimum Price (points)</Form.Label>
            <Form.Control name="minimumPrice" accepter={NumberInput} min={0} />
            <Form.HelpText>
              The lowest price a seller may set. Keep it high enough that a percentage commission never rounds
              down to zero.
            </Form.HelpText>
          </Form.Group>
        </Form>

        {previewResult && (
          <Message
            type={previewResult.commission === 0 ? 'warning' : 'info'}
            style={{ marginTop: 20 }}
            header="At the minimum price"
          >
            A buyer pays {previewResult.pricePaid} points, the platform burns {previewResult.commission}, and the
            seller receives {previewResult.sellerAmount}.
            {previewResult.commission === 0 && ' No commission would be taken — raise the minimum price.'}
          </Message>
        )}

        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default CommissionPolicyDrawer;
